import React, { useCallback, useEffect, useRef, useState } from "react";
import { ADMIN_TABLE_FETCH_SIZE } from "../constants";
import useDebounce from "../hooks/use-debounce";
import {
    deleteDoc,
    doc,
    getDocs,
    limit,
    orderBy,
    query,
    startAfter,
    Timestamp,
} from "firebase/firestore";
import {
    errorLogsCollection,
} from "../utils/firebase";
import { ChevronDown, ChevronUp, Copy, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

function formatTimestamp(timestamp) {
    if (!timestamp) return "-";
    const ts =
        timestamp instanceof Timestamp
            ? timestamp
            : timestamp?._seconds
            ? Timestamp.fromMillis(timestamp._seconds * 1000)
            : null;
    if (!ts) return "-";
    const date = ts.toDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours < 12 ? "AM" : "PM";
    hours = hours % 12 || 12;
    return `${month} ${day}, ${year} ${hours}:${String(minutes).padStart(2, "0")} ${period}`;
}

export default function ErrorLogs() {
    const [logs, setLogs] = useState(null);
    const [lastDoc, setLastDoc] = useState(null);
    const [sort, setSort] = useState("timestamp");
    const [order, setOrder] = useState("desc");
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    const size = ADMIN_TABLE_FETCH_SIZE;
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const observerRef = useRef(null);
    const loadingRef = useRef(false);

    const handleTableHeaderClick = (field) => {
        if (field === sort) {
            setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSort(field);
            setOrder("desc");
        }
    };

    const buildQuery = useCallback(
        (reset = false) => {
            const constraints = [orderBy(sort, order), limit(size)];
            if (!reset && lastDoc) {
                constraints.push(startAfter(lastDoc));
            }
            return query(errorLogsCollection, ...constraints);
        },
        [lastDoc, order, size, sort]
    );

    const fetchData = useCallback(
        async (reset = false) => {
            if (loadingRef.current) return;
            loadingRef.current = true;
            setLoading(true);

            try {
                const q = buildQuery(reset);
                const snapshot = await getDocs(q);
                const fetched = (snapshot?.docs ?? []).map((d) => ({
                    id: d.id,
                    ...d.data(),
                }));

                setLogs((prev) => {
                    if (reset) return fetched;
                    const seen = new Set(prev.map((l) => l.id));
                    const merged = [...prev];
                    for (const log of fetched) {
                        if (!seen.has(log.id)) {
                            seen.add(log.id);
                            merged.push(log);
                        }
                    }
                    return merged;
                });

                const last = snapshot.docs[snapshot.docs.length - 1] ?? null;
                setLastDoc(last);
                setHasMore(snapshot.docs.length === size);
            } catch (err) {
                console.error("Error fetching error logs:", err);
                toast.error("Failed to load error logs");
            }
            loadingRef.current = false;
            setLoading(false);
        },
        [buildQuery, size]
    );

    const handleDelete = async (e, logId) => {
        e.stopPropagation();
        if (!window.confirm("Delete this error log?")) return;
        try {
            await deleteDoc(doc(errorLogsCollection, logId));
            setLogs((prev) => prev.filter((l) => l.id !== logId));
            toast.success("Log deleted");
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete log");
        }
    };

    const buildCopyableText = (log) => {
        const knownKeys = new Set([
            "id",
            "timestamp",
            "action",
            "errorMessage",
            "errorCode",
            "userId",
            "eventId",
            "receiptType",
            "hasEntry",
            "hasWeightClass",
            "url",
            "userAgent",
        ]);
        const lines = [
            `Error Log ID: ${log.id ?? "-"}`,
            `Timestamp: ${formatTimestamp(log.timestamp)}`,
            `Action: ${log.action ?? "-"}`,
            `Error Message: ${log.errorMessage ?? "-"}`,
            `Error Code: ${log.errorCode ?? "-"}`,
            `User ID: ${log.userId ?? "-"}`,
            `Event ID: ${log.eventId ?? "-"}`,
            `Receipt Type: ${log.receiptType ?? "-"}`,
            `Has Entry: ${String(log.hasEntry ?? "-")}`,
            `Has Weight Class: ${String(log.hasWeightClass ?? "-")}`,
            `URL: ${log.url ?? "-"}`,
            `User Agent: ${log.userAgent ?? "-"}`,
        ];
        for (const key of Object.keys(log)) {
            if (knownKeys.has(key)) continue;
            const val = log[key];
            lines.push(
                `${key}: ${val === null || val === undefined ? "-" : String(val)}`
            );
        }
        return lines.join("\n");
    };

    const handleCopy = async (e, log) => {
        e.stopPropagation();
        try {
            const text = buildCopyableText(log);
            await navigator.clipboard.writeText(text);
            toast.success("Error log copied to clipboard");
        } catch (err) {
            console.error(err);
            toast.error("Failed to copy");
        }
    };

    useEffect(() => {
        setLogs([]);
        setLastDoc(null);
        setHasMore(true);
        fetchData(true);
    }, [sort, order]);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting && hasMore && !loadingRef.current) {
                fetchData();
            }
        });

        const el = observerRef.current;
        if (el) observer.observe(el);
        return () => {
            if (el) observer.unobserve(el);
        };
    }, [hasMore, fetchData]);

    const filteredLogs = React.useMemo(() => {
        if (!logs) return [];
        const term = (debouncedSearchTerm ?? "").trim().toLowerCase();
        if (!term) return logs;
        return logs.filter(
            (l) =>
                (l.userId ?? "").toLowerCase().includes(term) ||
                (l.action ?? "").toLowerCase().includes(term) ||
                (l.errorMessage ?? "").toLowerCase().includes(term)
        );
    }, [logs, debouncedSearchTerm]);

    const headers = {
        timestamp: "Timestamp",
        action: "Action",
        errorMessage: "Error Message",
        errorCode: "Error Code",
        userId: "User ID",
    };

    if (!logs) {
        return (
            <div className="page-size flex-center">
                <div className="loading loading-md loading-bars" />
            </div>
        );
    }

    return (
        <div className="page-size flex flex-col gap-4 p-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                <p className="font-bold text-xl">Error Logs</p>
                <input
                    type="text"
                    className="input input-bordered w-full md:w-72"
                    placeholder="Search by user ID, action, or error message..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="overflow-auto border rounded-lg">
                <table className="table min-w-full">
                    <thead>
                        <tr className="[&>th]:text-center">
                            <th className="w-8"></th>
                            {Object.entries(headers).map(([key, label]) => (
                                <th
                                    key={key}
                                    className="hover:bg-base-200 cursor-pointer"
                                    onClick={() => handleTableHeaderClick(key)}
                                >
                                    <div className="flex flex-row justify-center items-center gap-2">
                                        <span>{label}</span>
                                        {sort === key &&
                                            (order === "desc" ? (
                                                <ChevronDown />
                                            ) : (
                                                <ChevronUp />
                                            ))}
                                    </div>
                                </th>
                            ))}
                            <th className="w-20"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLogs.map((log) => (
                            <React.Fragment key={log.id}>
                                <tr
                                    className="hover cursor-pointer [&>td]:align-top"
                                    onClick={() =>
                                        setExpandedId((prev) =>
                                            prev === log.id ? null : log.id
                                        )
                                    }
                                >
                                    <td className="text-center font-bold">
                                        {order === "desc" ? (
                                            <ChevronDown className="inline" />
                                        ) : (
                                            <ChevronUp className="inline" />
                                        )}
                                    </td>
                                    <td className="text-sm">
                                        {formatTimestamp(log.timestamp)}
                                    </td>
                                    <td className="font-mono text-sm">
                                        {log.action ?? "-"}
                                    </td>
                                    <td className="max-w-[200px] truncate text-sm">
                                        {log.errorMessage ?? "-"}
                                    </td>
                                    <td className="font-mono text-xs opacity-80">
                                        {log.errorCode ?? "-"}
                                    </td>
                                    <td className="font-mono text-xs max-w-[120px] truncate">
                                        {log.userId ?? "-"}
                                    </td>
                                    <td>
                                        <div
                                            className="flex justify-center gap-1"
                                            onClick={(e) =>
                                                e.stopPropagation()
                                            }
                                        >
                                            <button
                                                className="btn btn-ghost btn-xs btn-square"
                                                onClick={(e) =>
                                                    handleCopy(e, log)
                                                }
                                                title="Copy error log"
                                            >
                                                <Copy size={16} />
                                            </button>
                                            <button
                                                className="btn btn-ghost btn-xs btn-square text-error"
                                                onClick={(e) =>
                                                    handleDelete(e, log.id)
                                                }
                                                title="Delete"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                {expandedId === log.id && (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="bg-base-200 p-4 text-sm"
                                        >
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                <p>
                                                    <span className="font-bold">
                                                        User ID:
                                                    </span>{" "}
                                                    {log.userId ?? "-"}
                                                </p>
                                                <p>
                                                    <span className="font-bold">
                                                        Action:
                                                    </span>{" "}
                                                    {log.action ?? "-"}
                                                </p>
                                                <p className="md:col-span-2">
                                                    <span className="font-bold">
                                                        Error Message:
                                                    </span>{" "}
                                                    {log.errorMessage ?? "-"}
                                                </p>
                                                <p>
                                                    <span className="font-bold">
                                                        Error Code:
                                                    </span>{" "}
                                                    {log.errorCode ?? "-"}
                                                </p>
                                                <p>
                                                    <span className="font-bold">
                                                        Event ID:
                                                    </span>{" "}
                                                    {log.eventId ?? "-"}
                                                </p>
                                                <p>
                                                    <span className="font-bold">
                                                        Receipt Type:
                                                    </span>{" "}
                                                    {log.receiptType ?? "-"}
                                                </p>
                                                <p>
                                                    <span className="font-bold">
                                                        Has Entry:
                                                    </span>{" "}
                                                    {String(
                                                        log.hasEntry ?? "-"
                                                    )}
                                                </p>
                                                <p>
                                                    <span className="font-bold">
                                                        Has Weight Class:
                                                    </span>{" "}
                                                    {String(
                                                        log.hasWeightClass ??
                                                            "-"
                                                    )}
                                                </p>
                                                <p className="md:col-span-2">
                                                    <span className="font-bold">
                                                        URL:
                                                    </span>{" "}
                                                    {log.url ?? "-"}
                                                </p>
                                                <p className="md:col-span-2">
                                                    <span className="font-bold">
                                                        User Agent:
                                                    </span>{" "}
                                                    <span className="break-all">
                                                        {log.userAgent ?? "-"}
                                                    </span>
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                <div className="flex-center w-full p-2">
                    {loading && (
                        <div className="loading loading-bars loading-sm" />
                    )}
                    {!loading && filteredLogs.length === 0 && (
                        <p className="opacity-70">No error logs found</p>
                    )}
                    {!loading &&
                        filteredLogs.length > 0 &&
                        !hasMore &&
                        searchTerm.trim() === "" && (
                            <p className="opacity-70">End of list</p>
                        )}
                </div>
                <div ref={observerRef} style={{ height: "1px" }} />
            </div>
        </div>
    );
}
